import React, { useEffect, useState, useRef } from "react";
import useMainContentDimensions from "../../../utils/mainContentDimensions";
import {
  useTheme,
  useMediaQuery,
  Grid,
  Card,
  CardHeader,
  CardContent,
  Typography,
  Box,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import useWindowDimensions from "../../../utils/windowDimensions";
import useAgreementTracker from "../../../hooks/useAgreementTracker";
import useAuth from "../../../hooks/useAuth";
import { formatDate, addDays } from "../../../utils/common";
import IconButton from "@mui/material/IconButton";
import DownloadIcon from "@mui/icons-material/Download";
import { useDispatch } from "react-redux";
import axios from "../../../utils/axios";
import { showSuccessSnackbar, showErrorSnackbar } from "../../../redux/slices/snackbar";

function AED() {
  const theme = useTheme();
  const dispatch = useDispatch();
  // Media queries for responsiveness
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md")); // Tablet devices
  const isLaptopM = useMediaQuery(theme.breakpoints.between("md", "lg")); // Laptop Medium (1280px–1440px)
  const isLaptopL = useMediaQuery(theme.breakpoints.up("lg")); // Laptop Large (1440px and above)

  const { user } = useAuth();
  const { width: mainContentWidth } = useMainContentDimensions();

  const currentFilters = [];

  if (user && user.businessType !== "SUPER_ADMIN" && user.tenantId) {
    currentFilters.push({
      fieldName: user.businessType === "MULTI_SERVICE_OPERATOR" ? "msoTenantId" : "tenantId",
      fieldValue: user.tenantId,
      op: "eq",
      dataType: "long",
      condition: "and",
      isJoin: true,
    });
  }

  const currentPayload = {
    filters: currentFilters,
    pagination: {
      pageNumber: 1,
      pageSize: 5,
    },
    sortFields: [
      {
        field: "dateOfExpiry",
        sort: "ASC",
      },
    ],
  };

  const [paginationModel, setPaginationModel] = React.useState({
    page: 0,
    pageSize: 5,
  });

  const [sortModel, setSortModel] = useState([
    {
      field: "dateOfExpiry",
      sort: "ASC",
    },
  ]);

  const { height } = useWindowDimensions();
  const { getAllAgreementTracker, agreementTrackers, paging, isLoading } = useAgreementTracker();
  const handleDownload = async (agreementTrackerId) => {
    try {
      const response = await axios.get(`/agreement-tracker/download/${agreementTrackerId}`, {
        responseType: "blob",
      });

      const url = window.URL.createObjectURL(response.data);

      const link = document.createElement("a");
      link.href = url;
      link.download = "agreement.pdf";

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      window.URL.revokeObjectURL(url);

      dispatch(showSuccessSnackbar("Downloaded successfully"));
    } catch (error) {
      dispatch(showErrorSnackbar(error?.response?.data?.message || "Download failed"));
    }
  };
  const { totalNumberOfRecords, hasNextPage, pageNumber, pageSize } = paging;

  const [payload, setPayload] = useState(currentPayload);

  const fetchData = async () => {
    if (Object.keys(payload).length !== 0) {
      await getAllAgreementTracker(payload);
    }
  };

  const onPageChange = (newPaginationModel) => {
    console.log(newPaginationModel, "pagepage");
    const pagination = {
      pageNumber: newPaginationModel.page === 0 ? 1 : newPaginationModel.page + 1,
      pageSize: payload.pagination.pageSize,
    };
    setPayload((prev) => ({ ...prev, pagination }));
    setPaginationModel(newPaginationModel);
  };

  useEffect(() => {
    const fetchRealTimeData = async () => {
      const filters = [];

      if (user && user.businessType !== "SUPER_ADMIN" && user.tenantId) {
        filters.push({
          fieldName: user.businessType === "MULTI_SERVICE_OPERATOR" ? "msoTenantId" : "tenantId",
          fieldValue: user.tenantId,
          op: "eq",
          dataType: "long",
          condition: "and",
          isJoin: true,
        });
      }

      const newPayload = {
        filters,
        pagination: { pageNumber: 1, pageSize: 5 },
        sortFields: [{ field: "dateOfExpiry", sort: "ASC" }],
      };

      setPayload(newPayload);
      await getAllAgreementTracker(newPayload);
    };

    if (user) {
      fetchRealTimeData();
    }
  }, [user]);

  useEffect(() => {
    if (payload !== currentPayload) {
      fetchData();
    }
  }, [payload]);

  const handleSortModelChange = (newModel) => {
    if (newModel.length !== 0) {
      setSortModel(newModel);

      const sortFields = newModel.map((item) => ({
        field: item.field,
        sort: item.sort?.toUpperCase(),
      }));

      setPayload((prev) => ({
        ...prev,
        sortFields,
      }));
    }
  };

  const columns = [
    {
      field: "tenant",
      headerName: "Franchise Name",
      valueGetter: (params) => params.tenantName,
      width: isTablet ? 150 : isLaptopM ? 200 : isLaptopL ? 220 : 180, // Adjust column width
    },
    {
      field: "dateOfExpiryStr",
      headerName: "Agreement Expiry Date",
      width: isTablet ? 140 : isLaptopM ? 180 : isLaptopL ? 200 : 160, // Adjust column width
    },

    {
      field: "actions",
      headerName: "Actions",
      width: 120,
      sortable: false,
      renderCell: (params) => {
        const filePath = params.row.agreementFilePath;

        if (!filePath) {
          return (
            <IconButton disabled>
              <DownloadIcon sx={{ opacity: 0.3 }} />
            </IconButton>
          );
        }

        return (
          <IconButton
            onClick={() => handleDownload(params.row.agreementTrackerId)}
            sx={{ color: "#1976d2" }}
          >
            <DownloadIcon />
          </IconButton>
        );
      },
    },
  ];

  const paginationMetaRef = useRef();

  // Memoize to avoid flickering when the `hasNextPage` is `undefined` during refetch
  const paginationMeta = React.useMemo(() => {
    if (hasNextPage !== undefined && paginationMetaRef.current?.hasNextPage !== hasNextPage) {
      paginationMetaRef.current = { hasNextPage };
    }
    return paginationMetaRef.current;
  }, [hasNextPage]);

  return (
    <Grid item xs={12} md={6}>
      <Card
        sx={{
          width: "100%",
          borderRadius: "20px",
          backgroundColor: (theme) => theme.palette.background.paper,
          boxShadow: (theme) =>
            theme.palette.mode === "dark"
              ? "0 10px 40px -10px rgba(0, 0, 0, 0.4)"
              : "0 10px 40px -10px rgba(0, 0, 0, 0.1)",
          overflow: "hidden",
          border: (theme) =>
            theme.palette.mode === "dark" ? "1px solid rgba(255, 255, 255, 0.08)" : "none",
        }}
      >
        <Box sx={{ p: { xs: 3, sm: 4 } }}>
          <Box sx={{ mb: 4 }}>
            <Typography variant="h4" sx={{ fontWeight: 700, color: "text.primary" }}>
              Agreement Expiry Details
            </Typography>
          </Box>
          <Box sx={{ height: 420 }}>
            <DataGrid
              autoHeight
              initialState={{
                pagination: { paginationModel: { pageSize: 5 } },
              }}
              columns={columns}
              rows={agreementTrackers}
              getRowId={(row) => row["agreementTrackerId"]}
              rowCount={paging?.totalNumberOfRecords}
              loading={isLoading}
              rowsPerPageOptions={[5]}
              pagination
              page={paging?.pageNumber}
              pageSize={5}
              paginationMode="server"
              onPaginationModelChange={onPageChange}
              paginationMeta={paginationMeta}
              sortingMode="server"
              sortModel={sortModel}
              onSortModelChange={handleSortModelChange}
              paginationModel={paginationModel}
              getRowHeight={() => 64}
              slots={{
                noRowsOverlay: () => (
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      height: "100%",
                      gap: 2,
                    }}
                  >
                    <Box
                      sx={{
                        width: 64,
                        height: 64,
                        borderRadius: "50%",
                        backgroundColor: "#f1f5f9",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Box
                        component="span"
                        sx={{ fontSize: "24px", opacity: 0.5 }}
                        role="img"
                        aria-label="calendar"
                      >
                        📅
                      </Box>
                    </Box>
                    <Typography variant="body2" sx={{ color: "#64748b", fontWeight: 500 }}>
                      No agreement expiry found
                    </Typography>
                  </Box>
                ),
              }}
              sx={{
                border: "none",
                "& .MuiDataGrid-columnHeaders": {
                  backgroundColor: (theme) =>
                    theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.05)" : "#f8fafc",
                  borderBottom: (theme) =>
                    theme.palette.mode === "dark"
                      ? "1px solid rgba(255, 255, 255, 0.08)"
                      : "1px solid #f1f5f9",
                  color: (theme) =>
                    theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.8)" : "#64748b",
                  fontSize: "12px",
                  fontWeight: 700,
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                },
                "& .MuiDataGrid-cell": {
                  borderBottom: (theme) =>
                    theme.palette.mode === "dark"
                      ? "1px solid rgba(255, 255, 255, 0.08)"
                      : "1px solid #f1f5f9",
                  fontSize: "14px",
                  color: "text.primary",
                },
                "& .MuiDataGrid-row:hover": {
                  backgroundColor: (theme) =>
                    theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.04)" : "#f8fafc",
                },
                "& .MuiDataGrid-footerContainer": {
                  borderTop: "none",
                },
                "& .MuiDataGrid-columnSeparator": {
                  display: "none",
                },
              }}
            />
          </Box>
        </Box>
      </Card>
    </Grid>
  );
}

export default AED;
